/**
 * Contrat d'API — Console tuteur (PRD Module 10, lots B7/B8/B9/B19 de la
 * grille `docs/produit/EXPERIENCE-PARENT-TUTEUR.md` §13.3).
 *
 * Trois idées portent tout ce fichier ; elles viennent de la conception validée
 * (§10.1, §2.3) et de la maquette v5 `docs/maquettes/v5/tuteur.html` (écran T-1) :
 *
 *  1. **Le serveur décide, le client affiche.** Le KPI du PRD est « quinze
 *     élèves priorisés en moins de trois minutes ». Un tableau ne priorise pas,
 *     il présente : il rend la priorisation au tuteur. `FileTuteurResponse` n'est
 *     donc PAS une liste à trier — c'est une file déjà ordonnée, bornée, où
 *     chaque unité porte son rang, son motif rédigé et une action unique.
 *  2. **Le motif est une phrase, jamais un score.** Aucune unité ne porte de
 *     note, de pourcentage ni de « niveau de priorité » numérique. Le tuteur
 *     doit pouvoir CONTESTER la priorité : un tri opaque n'est jamais adopté par
 *     un professionnel (PRIV-10, transparence des décisions automatisées). D'où
 *     `critere` / `criteresParType`, sur le patron de `CRITERE_RECOMMANDATION`
 *     (`suivi.service.ts`), rendus tels qu'ils sont écrits dans le code.
 *  3. **Le décompte des écartés est aussi important que la file.** C'est ce qui
 *     permet au tuteur de faire confiance à la file sans aller vérifier :
 *     « 7 avancent », « 3 ont travaillé hier », « 2 sous le seuil de 7 jours ».
 *
 * Invariant produit rappelé (fondateur, à plusieurs reprises) : JAMAIS de niveau
 * agrégé unique, JAMAIS de moyenne, JAMAIS de pourcentage de maîtrise.
 * Uniquement les cinq paliers discrets par savoir PDA (`NIVEAUX_MAITRISE`) ou
 * des paniers dénombrés (`PaniersSavoirsTuteurDto`). La confiance du moteur elle-même
 * n'est jamais exposée en nombre : elle est qualifiée en mots
 * (`QUALIFICATIFS_CONFIANCE`).
 *
 * Hors périmètre, sur décision explicite du fondateur : AUCUNE MESSAGERIE
 * (B16 annulé) — ni tuteur↔élève, ni tuteur↔parent. Le seul texte que le tuteur
 * adresse à la famille par ce module est le `commentaire` facultatif attaché à
 * une évaluation revue (`RevueEvaluationRequest.commentaire`) : une annotation
 * de copie, sans réponse possible, qui n'ouvre aucun fil.
 *
 * Uniquement des formes de données. Validation d'entrée, priorisation et
 * rédaction des motifs vivent côté `@silex/api` (`modules/tutor/`).
 *
 * NOTE orchestrateur : nouveau fichier — à ajouter au barrel
 * `packages/shared/src/types/index.ts`.
 */
/* ------------------------------------------------------------- libellés */
/**
 * Libellé français d'un palier de maîtrise, tel qu'il doit apparaître à l'écran
 * (maquette v5 T-2/T-3). Source unique de vérité : le serveur rédige les motifs
 * avec ces mots-là, le client affiche les mêmes.
 */
export const LIBELLES_NIVEAU_MAITRISE = {
    non_evalue: 'non évalué',
    en_decouverte: 'en découverte',
    en_consolidation: 'en consolidation',
    en_voie_de_maitrise: 'en voie de maîtrise',
    maitrise: 'maîtrisé',
};
/**
 * La confiance du moteur est un flottant en base (`MaitriseSavoir.confiance`).
 * Elle n'est JAMAIS exposée en nombre : un tuteur ne décide pas sur « 0,62 ».
 * Elle sort qualifiée, et le seuil est documenté côté service.
 */
export const QUALIFICATIFS_CONFIANCE = ['faible', 'moyenne', 'elevee'];
/* ------------------------------------------ B8 — GET /v1/tuteur/file (T-1) */
/**
 * Les quatre types d'unités, **dans leur ordre de priorité** (§10.2). L'ordre du
 * tableau EST le critère de tri primaire : le serveur ne renvoie jamais un score.
 *  1. `signal_securite`      — détresse d'un mineur. Toujours en tête, jamais
 *                              regroupé, JAMAIS reportable.
 *  2. `evaluation_a_valider` — un verdict d'IA attend une décision humaine.
 *  3. `eleve_a_relancer`     — inactivité au-delà du seuil.
 *  4. `blocage_savoir`       — le même savoir repris sans que le palier bouge.
 */
export const TYPES_UNITE_TRAVAIL = [
    'signal_securite',
    'evaluation_a_valider',
    'eleve_a_relancer',
    'blocage_savoir',
];
/**
 * Borne dure de la file. « Une file infinie n'est pas une file, c'est un tableau
 * déguisé — et une file qu'on ne vide jamais est la première raison de cesser de
 * l'ouvrir » (§10.2). L'excédent est ANNONCÉ (`FileTuteurResponse.excedent`),
 * jamais servi dans la file du jour.
 */
export const MAX_UNITES_FILE = 6;
/**
 * Jours sans séance à partir desquels un élève entre dans la file en
 * `eleve_a_relancer`. En dessous, relancer serait du bruit — et c'est dit au
 * tuteur dans le décompte des écartés, pas caché.
 */
export const SEUIL_JOURS_RELANCE = 7;
/**
 * Nombre d'observations sur un même savoir à partir duquel une reprise sans
 * changement de palier devient un `blocage_savoir`. En dessous, c'est de
 * l'entraînement normal, pas un blocage.
 *
 * Ces observations doivent en outre tomber sur `SEUIL_RECURRENCE` JOURS
 * DISTINCTS dans les `FENETRE_RECURRENCE_JOURS` derniers jours
 * (`rapport-savoir.ts`) : la définition de la récurrence du produit vaut aussi
 * pour « Blocage à traiter » (`UX-7-03`, Φ-MV-7). Six essais le même soir sont
 * une séance de travail, pas un blocage.
 */
export const SEUIL_REPRISES_BLOCAGE = 3;
/**
 * Jours au-delà desquels un savoir « à surveiller » qui n'a plus bougé cesse
 * d'être un blocage actif : il devient une recommandation d'intervention servie
 * par `GET /v1/suivi/:eleveId/recommandations`
 * (`SEUIL_JOURS_SANS_PROGRES_RECOMMANDATION`). Un blocage se traite à chaud.
 */
export const FENETRE_JOURS_BLOCAGE_ACTIF = 14;
/** Temps annoncé par unité (§2.3 : « terminable en moins de cinq minutes »). */
export const DUREE_ESTIMEE_MINUTES = {
    signal_securite: 5,
    evaluation_a_valider: 2,
    eleve_a_relancer: 3,
    blocage_savoir: 4,
};
/**
 * UNE action par unité, jamais deux au même niveau. Un écran qui ne se termine
 * pas par « fait » est un écran que le tuteur n'ouvre pas (§2.3).
 */
export const ACTIONS_UNITE_TRAVAIL = [
    'ouvrir_signal',
    'valider_evaluation',
    'creer_exercices_cibles',
    'ecrire_mot_du_tuteur',
];
/**
 * Pourquoi un élève suivi n'est PAS dans la file ce matin. Quatre paniers
 * exclusifs et exhaustifs : leur somme + le nombre d'élèves porteurs d'une unité
 * (file, excédent ou report) vaut exactement `nbElevesSuivis`. C'est cette
 * exhaustivité qui rend la file digne de confiance.
 */
export const MOTIFS_ECART = [
    'avance',
    'travail_recent',
    'inactivite_sous_seuil',
    'jamais_actif',
];
/** Deux gestes, pas trois. Toute étape supplémentaire tue le KPI des 20 minutes (§10.5). */
export const DECISIONS_REVUE = ['confirme', 'corrige'];
/** Nombre total de paliers de l'échelle d'indices (3 indices + la révélation). */
export const INDICES_TOTAL_AIDE_DEVOIRS = 4;
//# sourceMappingURL=tuteur.js.map